import React from 'react';

import { Select } from '@aline/common';
import { link, routes } from '@aline/routes';
import { Typography } from '@mui/material';
import type { GridCellParams } from '@mui/x-data-grid-premium';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import type { StorableIconDocument } from 'src/components';
import { documentListButtonId, QuickViewButton, Spacer, StorableIcon } from 'src/components';
import { FavoriteStarIcon } from 'src/components/icons/FavoriteStarIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import { openLinkFromAnchorClick, usePartyParams } from 'src/helpers';
import { createClampStyles } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

const useNameStyles = makeStyles()(theme => ({
  cell: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(0.5),
    overflow: 'hidden',
    width: '100%',
  },
  nameContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    overflow: 'hidden',
  },
  nameLink: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: 0,
    overflow: 'hidden',
    textDecoration: 'none',
  },
  nameText: {
    ...createClampStyles(2),
    color: theme.appColors.iconsType,
  },
}));

interface PartyDocumentRow extends StorableIconDocument {
  id: UUID;
  name: string | null;
  requesterFavorited: boolean;
}

export function PartyDocumentNameCell<T extends PartyDocumentRow>({
  row,
  value,
}: GridCellParams<T, string | null>): JSX.Element {
  const { classes } = useNameStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { partyId } = usePartyParams();
  const previewMatch = matchPath(routes.PartyDetailDocumentPreview, location.pathname);
  const isCurrentDocumentOpen = previewMatch?.params.documentId === row.id;
  const documentHref = link.DocumentDetail({ documentId: row.id });
  const title = value || 'Untitled Document';

  const handleQuickViewClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    const nextLink = isCurrentDocumentOpen
      ? link.PartyDetailDocuments({ partyId })
      : link.PartyDetailDocumentPreview({ documentId: row.id, partyId });
    navigate(nextLink, { replace: true });
  };

  return (
    <div className={classes.cell}>
      <div className={classes.nameContainer}>
        <Link
          className={classes.nameLink}
          to={documentHref}
          onClick={e => openLinkFromAnchorClick(e, documentHref, { title })}
        >
          <StorableIcon storable={row} />
          <Typography noWrap className={classes.nameText} variant="subtitle2">
            {value || 'Untitled document'}
          </Typography>
        </Link>
        {row.requesterFavorited && <FavoriteStarIcon fill={IconColors.StarYellow} />}
      </div>
      <Spacer />
      <QuickViewButton
        data-cy={Select.repository.quickViewDocument}
        id={documentListButtonId}
        tooltipMsg="Quick view document details"
        onClick={handleQuickViewClick}
      />
    </div>
  );
}
